import type { DashboardSummary } from '../services/crm/dashboardService';
import type { Deal } from '../services/crm/dealsService';
import type { Contact } from '../services/crm/contactsService';
import type { Company } from '../services/crm/companiesService';
import type { WorkflowRun } from '../services/crm/workflowsService';

export interface AdvisorDashboardSnapshot {
  totalWeighted: number;
  bestCase?: number;
  commit?: number;
  quotaTarget?: number;
  coverageRatio?: number;
  nextQuarterProjection?: number;
  owners?: Array<{ owner: string; weighted: number; commit: number }>;
  cashflowSeries?: Array<{ date: string; amount: number }>;
}

export function buildAdvisorDashboardSnapshot(summary: DashboardSummary | null | undefined): AdvisorDashboardSnapshot | null {
  if (!summary?.forecast) return null;
  const { forecast } = summary;
  return {
    totalWeighted: Number(forecast.weightedPipeline || 0),
    bestCase: typeof forecast.bestCase === 'number' ? forecast.bestCase : undefined,
    commit: typeof forecast.commit === 'number' ? forecast.commit : undefined,
    quotaTarget: typeof forecast.quotaTarget === 'number' ? forecast.quotaTarget : undefined,
    coverageRatio: typeof forecast.coverageRatio === 'number' ? forecast.coverageRatio : undefined,
    nextQuarterProjection: typeof forecast.nextQuarterProjection === 'number' ? forecast.nextQuarterProjection : undefined,
    owners: Array.isArray(forecast.forecastByOwner)
      ? forecast.forecastByOwner.map((row) => ({
        owner: String(row.owner || 'Unknown'),
        weighted: Number(row.weighted || 0),
        commit: Number(row.commit || 0)
      }))
      : undefined,
    cashflowSeries: Array.isArray(forecast.cashflowSeries)
      ? forecast.cashflowSeries.map((row) => ({
        date: row.date,
        amount: Number(row.amount || 0)
      }))
      : undefined
  };
}

export interface AdvisorDealRisk {
  id: string;
  title: string;
  owner: string;
  stageId: string;
  probability?: number;
  weightedAmount?: number;
  arrAmount?: number;
  lastUpdated: string;
}

export function getAtRiskDeals(deals: Deal[] | null | undefined, opts?: { probabilityThreshold?: number; staleDays?: number }): AdvisorDealRisk[] {
  if (!Array.isArray(deals)) return [];
  const probabilityThreshold = typeof opts?.probabilityThreshold === 'number' ? opts.probabilityThreshold : 0.4;
  const staleDays = typeof opts?.staleDays === 'number' ? opts.staleDays : 14;
  const cutoff = Date.now() - staleDays * 24 * 60 * 60 * 1000;

  return deals
    .filter((deal) => {
      const pct = typeof deal.stage_probability === 'number' ? deal.stage_probability : undefined;
      const updatedAt = new Date(deal.updated_at).getTime();
      const isStale = Number.isFinite(updatedAt) ? updatedAt < cutoff : false;
      const belowThreshold = pct !== undefined && pct < probabilityThreshold;
      return isStale || belowThreshold;
    })
    .map((deal) => ({
      id: deal.id,
      title: deal.title,
      owner: deal.owner_user_id,
      stageId: deal.stage_id,
      probability: typeof deal.stage_probability === 'number' ? deal.stage_probability : undefined,
      weightedAmount: typeof deal.weighted_amount === 'number' ? deal.weighted_amount : undefined,
      arrAmount: typeof deal.arr_amount === 'number' ? deal.arr_amount : undefined,
      lastUpdated: deal.updated_at
    }));
}

export interface AdvisorDataQualityScore {
  entityId: string;
  entityType: 'contact' | 'company';
  name: string;
  score: number;
  missingFields: string[];
  enrichmentConfidence?: number;
  enrichedAt?: string;
}

const CONTACT_FIELDS: Array<keyof Contact> = ['email', 'phone', 'linkedin_url', 'persona', 'lifecycle_stage'];
const COMPANY_FIELDS: Array<keyof Company> = ['domain', 'website', 'industry', 'size', 'annual_revenue', 'arr_potential', 'employee_count'];

function scoreEntity(required: string[], present: string[]): number {
  if (required.length === 0) return 100;
  const filled = required.filter((field) => present.includes(field)).length;
  return Math.round((filled / required.length) * 100);
}

export function buildContactDataQuality(contacts: Contact[] | null | undefined): AdvisorDataQualityScore[] {
  if (!Array.isArray(contacts)) return [];
  return contacts.map((contact) => {
    const present: string[] = [];
    CONTACT_FIELDS.forEach((field) => {
      const value = contact[field];
      if (value !== null && value !== undefined && value !== '') {
        present.push(field);
      }
    });
    const score = scoreEntity(CONTACT_FIELDS as string[], present);
    return {
      entityId: contact.id,
      entityType: 'contact',
      name: [contact.first_name, contact.last_name].filter(Boolean).join(' ') || contact.email || 'Contact',
      score,
      missingFields: (CONTACT_FIELDS as string[]).filter((field) => !present.includes(field)),
      enrichmentConfidence: typeof contact.enrichment_confidence === 'number' ? contact.enrichment_confidence : undefined,
      enrichedAt: contact.enriched_at
    };
  });
}

export function buildCompanyDataQuality(companies: Company[] | null | undefined): AdvisorDataQualityScore[] {
  if (!Array.isArray(companies)) return [];
  return companies.map((company) => {
    const present: string[] = [];
    COMPANY_FIELDS.forEach((field) => {
      const value = company[field];
      if (value !== null && value !== undefined && value !== '') {
        present.push(field);
      }
    });
    const score = scoreEntity(COMPANY_FIELDS as string[], present);
    return {
      entityId: company.id,
      entityType: 'company',
      name: company.name,
      score,
      missingFields: (COMPANY_FIELDS as string[]).filter((field) => !present.includes(field)),
      enrichmentConfidence: typeof company.enrichment_confidence === 'number' ? company.enrichment_confidence : undefined,
      enrichedAt: company.enriched_at
    };
  });
}

export interface AdvisorWorkflowAlert {
  runId: string;
  workflowId: string | null;
  triggerType: string;
  entityId: string;
  entityType: string;
  status: string;
  startedAt: string;
  finishedAt?: string;
}

export function buildWorkflowAlerts(runs: WorkflowRun[] | null | undefined): AdvisorWorkflowAlert[] {
  if (!Array.isArray(runs)) return [];
  return runs.map((run) => ({
    runId: run.id,
    workflowId: run.workflow_id,
    triggerType: run.trigger_type,
    entityId: run.entity_id,
    entityType: run.entity_type,
    status: run.status,
    startedAt: run.started_at,
    finishedAt: run.finished_at
  }));
}
